/**
 * ConflictWarning Component
 *
 * Displays a warning banner when contradictory information is found
 * in the source documents for a criterion evaluation.
 */

import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

interface ConflictWarningProps {
  hasContradiction: boolean;
  reasoning?: string;
}

export function ConflictWarning({ hasContradiction, reasoning }: ConflictWarningProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Only render if there is a contradiction
  if (!hasContradiction) {
    return null;
  }

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Contradictory Information Found
          </span>
        </div>
        {reasoning && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded transition-colors"
            aria-label={isExpanded ? 'Hide details' : 'Show details'}
          >
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>

      {/* Expanded details */}
      {isExpanded && reasoning && (
        <div className="px-3 pb-2 pt-0 border-t border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-800 dark:text-amber-300 mt-2">{reasoning}</p>
        </div>
      )}
    </div>
  );
}
